# Privacy Policy

*Last updated: October 23, 2026*

This Privacy Policy describes how **Yanick Senn** ("we", "us", or "our") collects, uses, stores, and protects your information when you use the **kart.win** web platform ([https://kart.win](https://kart.win)) and the **Kart Win Extractor** mobile application (`com.yanicksenn.telemetry.extractor`) for iOS and Android (collectively, the "Services").

---

## 1. Information We Collect

We collect only the information necessary to authenticate your account, extract motorsport telemetry sessions from your hardware data logger, synchronize and analyze those sessions, and maintain the reliability of the Services.

| Category | Data Elements | Source | Purpose |
| --- | --- | --- | --- |
| **Account & Identity** | Email address, display name, OAuth provider identifier (`google_id`, `apple_id`), account creation and login timestamps | Sign in with Apple / Google Sign-In | Account authentication and linking uploaded telemetry sessions to your profile |
| **Session Credentials** | Cryptographic hashes (`SHA-256`) of access and refresh tokens, token expiration timestamps | Generated upon sign-in | Maintaining authenticated sessions across web and mobile devices |
| **Kart Telemetry & Setup Data** | Raw logger files (`.xrz`, `.xrk`), logger serial number and firmware version, driver/vehicle/track identifiers, lap and sector times, speed, engine RPM, accelerations, temperatures, on-track GPS trajectories recorded by the kart logger, and linked mechanical kart setup snapshots | Hardware kart data loggers (e.g., AiM MyChron 5 / 5S / 6, Alfano) and user input | Visualizing, comparing, and storing motorsport telemetry and vehicle setups |
| **Diagnostics & Crash Reports** | Unhandled runtime error messages, stack traces, application URL/route, browser/device user-agent, screen dimensions, locale, and authenticated user identifier | Automatic client error reporter & voluntary bug reports | Diagnosing and fixing software bugs and upload failures |

### 1.1 Account & Authentication Data

When you sign in using **Sign in with Apple** or **Google Sign-In**, we receive your verified email address, display name (when provided by the identity provider), and a unique provider subject identifier. We do not receive or store your Apple ID or Google account password.

### 1.2 Local Wi-Fi Network Access & Hardware Data Loggers

The **Kart Win Extractor** mobile app requests local network access (`NSLocalNetworkUsageDescription` on iOS and local Wi-Fi socket access on Android) **exclusively** to communicate directly over Wi-Fi (`TCP` port `2000` and `UDP` port `36002`, typically at `10.0.0.1`) with onboard kart data loggers such as **AiM MyChron 5 / 5S / 6** and **Alfano** devices.

- We **do not** scan your home or local network for unrelated devices.
- We **do not** access or collect your mobile device's own GPS or location services. Any GPS coordinates present in uploaded telemetry files originate solely from the kart data logger's onboard GPS receiver recorded during track sessions.

### 1.3 On-Device Storage

The Services store a minimal set of data locally on your device (`localStorage` and temporary session buffers):

- Your active session authentication token and cached account profile so you remain signed in between trackside heats (including when offline at the circuit).
- Your selected interface language preference.
- Temporary buffers of downloaded telemetry sessions queued for upload while transitioning from the kart data logger's ad-hoc Wi-Fi network back to internet connectivity. Queued binaries are removed once uploaded.
- A local registry of previously imported session filenames so the app can badge already-downloaded sessions when reconnecting to your data logger.

---

## 2. How We Use Your Information

We use the collected information solely to provide, operate, and improve the Services:

1. **Authentication & Account Management**: Verifying your identity and securing access to your telemetry sessions, kart setups, inventory, and team workspaces.
2. **Telemetry Processing & Analysis**: Parsing uploaded logger archives, computing lap and corner metrics, comparing driving lines, and retrieving historical track weather conditions corresponding to session timestamps and circuit coordinates.
3. **Diagnostics & Reliability**: Identifying and resolving application crashes, protocol errors, and synchronization issues.

---

## 3. Data Sharing & Third-Party Services

> We do **not** sell, rent, or trade your personal information. The Services contain **no** third-party advertising SDKs, behavioral analytics trackers, or cross-app tracking technologies.

Your information is shared only in the following limited circumstances:

- **Authentication Providers**: Sign-in tokens are verified against official public keys published by **Apple Inc.** (Sign in with Apple) and **Google LLC** (Google Sign-In).
- **Cloud Infrastructure**: Account records and uploaded telemetry sessions are hosted on secure **Google Cloud Platform (GCP)** infrastructure.
- **Team & Workspace Sharing**: If you explicitly join a team or share telemetry sessions, setups, or equipment inventory within the kart.win platform, authorized members of that team will be able to view the shared motorsport data.
- **Legal Obligations**: When required by applicable law, regulation, or valid legal process.

---

## 4. Data Security

We implement industry-standard technical and organizational safeguards to protect your data:

- All communication between the Kart Win Extractor mobile app, the kart.win web portal, and our backend servers is encrypted in transit using **HTTPS / TLS**.
- Session access tokens and refresh tokens are never stored in plaintext in our database; only one-way **SHA-256** cryptographic hashes are persisted.
- Production databases and cloud services are isolated within access-controlled environments.

---

## 5. Data Retention & Account Deletion

We retain your account information and uploaded telemetry sessions for as long as your account remains active or as needed to provide the Services.

- **Managing Data in the App**: You can delete individual telemetry sessions, unlink authentication providers, clear local device upload queues and session registries, or revoke connected applications directly within the application settings.
- **Self-Serve Account & Full Data Deletion**: You can permanently delete your account and all associated data (including sessions, telemetry samples, saved analyses, equipment inventory, kart setups, teams, and shared session entries) directly at any time via the **Account Settings** page on [kart.win](https://kart.win/#/settings) (accessible directly on the web or via **Settings → Manage Account** in the Kart Win Extractor mobile app). Alternatively, you may request account deletion by contacting us at [info@yanicksenn.com](mailto:info@yanicksenn.com).

---

## 6. Children's Privacy

The Services are not directed to children under the age of 13 (or the minimum age required in your jurisdiction), and we do not knowingly collect personal information from children without verifiable parental consent. If you believe that personal information of a child has been provided to us improperly, please contact us at [info@yanicksenn.com](mailto:info@yanicksenn.com) so we can delete it promptly.

---

## 7. Changes to This Privacy Policy

We may update this Privacy Policy from time to time to reflect changes in our features, practices, or legal requirements. When we make updates, we will revise the "Last updated" date at the top of this page.

---

## 8. Contact Us

If you have any questions, concerns, or requests regarding this Privacy Policy or your data, please contact:

- **Developer**: Yanick Senn
- **Email**: [info@yanicksenn.com](mailto:info@yanicksenn.com)
- **Web**: [https://kart.win](https://kart.win)
